import { ChangeEvent, useEffect, useState } from 'react';
import { InputProps } from '../OdfjellInputComponent';

type NumberInputVM = {
    stringValue: string | undefined;
    numberValue: number | undefined;
};

const NumberInputComponent: React.FC<Partial<InputProps>> = ({
    value,
    leaveBlank,
    allowNegativeValues,
    min,
    max,
    name,
    tabIndex,
    elementId,
    onChange,
    maxDecimals,
    inputSize,
}: Partial<InputProps>) => {
    const [numberInputVm, setNumberInputVm] = useState<NumberInputVM>({
        stringValue: undefined,
        numberValue: undefined,
    });

    useEffect(() => {
        if (value !== undefined && value !== null && !isNaN(Number(value))) {
            const _value = Number(value);
            setNumberInputVm({ stringValue: _value.toString(), numberValue: _value });
        } else {
            const _value = leaveBlank ? undefined : 0;
            setNumberInputVm({ stringValue: _value?.toString(), numberValue: _value });
        }
    }, [value]);

    const getInputClasses = () => {
        const classes = 'number';

        if (inputSize && inputSize === 'small') {
            return `${classes} small-input`;
        } else if (inputSize && inputSize === 'large') {
            return `${classes} large-input`;
        }

        return `${classes}`;
    };

    const onNumberFocus = (event) => {
        const inputString = event.target.value;
        if (inputString === '0.00' || inputString === '0') {
            event.target.value = '';
        }
    };

    const onNumberLeaveFocus = (event) => {
        const inputString = event.target.value;
        if ((!inputString || inputString === '') && !leaveBlank) {
            event.target.value = '0';
            onChange({ event });
        }
    };

    const onNumberInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputString = event.target.value;
        event.preventDefault();
        const numberInputVm: NumberInputVM = { numberValue: +inputString, stringValue: inputString };
        setNumberInputVm({ ...numberInputVm });
        if (inputString.includes('.')) {
            const numDecimals = inputString.toString().split('.')[1].length || 0;
            if (numDecimals <= maxDecimals) {
                event.target.value = numberInputVm.stringValue ?? '';
                onChange({ event });
            }
        } else {
            onChange({ event });
        }
    };

    const handleClick = (event: any) => {
        event.target.select();
    };

    const onNumberKeyDown = (event) => {
        const inputString = event.target.value;
        const charCode = event.which;
        const char = String.fromCharCode(charCode);

        const isCommaOrDot = char === ',' || char === '.';
        const isNumber = char >= '0' && char <= '9';
        const isNegativeSign = char === '-';
        const isInvalidChar = !isNumber && !isCommaOrDot && !isNegativeSign;

        if (isCommaOrDot) {
            event.preventDefault();
            if (maxDecimals === 0) return;
            if (!inputString) {
                event.target.value += '0.';
            } else if (!inputString.includes('.')) {
                event.target.value += '.';
            }
        } else if (isInvalidChar) {
            event.preventDefault();
        } else if (!allowNegativeValues && isNegativeSign) {
            event.preventDefault();
        }
    };

    return (
        <input
            type="text"
            onKeyPress={onNumberKeyDown}
            pattern="^([0-9]*[.])?[0-9]*$"
            onFocus={onNumberFocus}
            onBlur={onNumberLeaveFocus}
            onChange={onNumberInputChange}
            min={min}
            max={max}
            value={numberInputVm.stringValue ?? ''}
            name={name}
            className={getInputClasses()}
            autoComplete="off"
            onClick={handleClick}
            id={elementId}
            tabIndex={tabIndex}
        ></input>
    );
};

export default NumberInputComponent;
